/**
 * The tenant's media-domain allowlist (ACP-D52, gfx/docs/plans/playout.md §7.2): the hosts a renderer may fetch
 * dynamic images from directly, and the only hosts the image fetch proxy (`fetchImage` on a control session) will
 * contact for it. A list of host patterns, saved whole by a graphics administrator
 * (`GET/PUT /graphics/settings/media-domains`); a renderer receives it with its control-session `welcome`.
 *
 * A pattern is one of
 *   - an exact host: `cdn.example.com` (at least two labels), or
 *   - a wildcard: `*.example.com`, any host with one or more labels in front of `example.com` — NOT `example.com`
 *     itself (list it too if wanted). The part after `*.` has at least two labels, so `*.com` is not a pattern.
 *
 * Hosts are lowercase letters, digits and hyphens in labels of 1–63 characters (a punycode `xn--` label is a label), at
 * most 253 characters in all. Refused: IP addresses (the last label is never all digits), ports, paths, schemes,
 * credentials, a trailing dot. Product-free and pure: the relay's fetch proxy and the CLI use the same functions.
 */
import { z } from 'zod';
import { isoTimestampWireSchema } from './primitives.js';

export const MAX_MEDIA_DOMAINS = 100;
const MAX_HOST_LENGTH = 253;
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** Whether `host` is a lowercase DNS name of at least two labels whose last label is not all digits (so never an IP). */
export function isMediaHost(host: string): boolean {
  if (host.length === 0 || host.length > MAX_HOST_LENGTH) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  if (!labels.every((label) => LABEL.test(label))) return false;
  const last = labels[labels.length - 1] ?? '';
  return !/^[0-9]+$/.test(last);
}

/** The reason `pattern` is not a host pattern, or null when it is one. */
export function mediaDomainPatternProblem(pattern: string): string | null {
  if (pattern !== pattern.toLowerCase()) return 'must be lowercase';
  const host = pattern.startsWith('*.') ? pattern.slice(2) : pattern;
  if (host.includes('*')) return 'may only use a wildcard as the first label (`*.example.com`)';
  if (!isMediaHost(host)) return 'must be a host name such as `cdn.example.com` or `*.example.com` (no IP address, port, path or scheme)';
  return null;
}

/** Whether `host` (lowercase, as a URL parser gives it) matches one pattern: exact, or a wildcard's proper subdomain. */
export function mediaHostMatches(host: string, pattern: string): boolean {
  if (!pattern.startsWith('*.')) return host === pattern;
  const suffix = pattern.slice(1); // `.example.com`
  return host.length > suffix.length && host.endsWith(suffix);
}

/** Whether `host` is on the list. */
export function mediaHostAllowed(host: string, patterns: readonly string[]): boolean {
  return patterns.some((pattern) => mediaHostMatches(host, pattern));
}

/** The list's canonical form: sorted, no duplicates. */
export function normaliseMediaDomains(patterns: readonly string[]): string[] {
  return [...new Set(patterns)].sort();
}

export const mediaDomainWireSchema = z
  .string()
  .min(1)
  .max(MAX_HOST_LENGTH + 2)
  .superRefine((value, ctx) => {
    const problem = mediaDomainPatternProblem(value);
    if (problem !== null) ctx.addIssue({ code: 'custom', message: `${JSON.stringify(value)} ${problem}` });
  });

export const graphicsMediaDomainsWireSchema = z
  .object({
    /** The allowlist, sorted. Empty: no host is allowed (renderers show the fallback; the proxy refuses every fetch). */
    domains: z.array(z.string()),
    /** When it was last saved; null when it never was. */
    updatedAt: isoTimestampWireSchema.nullable(),
  })
  .strict();
export type GraphicsMediaDomainsWire = z.infer<typeof graphicsMediaDomainsWireSchema>;

export const updateGraphicsMediaDomainsRequestWireSchema = z
  .object({
    /** The whole list, replacing the stored one (last write wins). Saved sorted and de-duplicated. */
    domains: z.array(mediaDomainWireSchema).max(MAX_MEDIA_DOMAINS),
  })
  .strict();
export type UpdateGraphicsMediaDomainsRequestWire = z.infer<typeof updateGraphicsMediaDomainsRequestWireSchema>;
